import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Trash2, Upload, X } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { FilterField } from '@/components/ui/FilterBar';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { apiError } from '@/lib/apiError';
import {
  clearBankDate, deleteStatement, deleteStatementLine, getBrs, getBrsBanks, getBrsUploads, importStatement, previewStatement, rematchBank, setBankDate,
  type BankMapping, type BrsLine, type BrsRow,
} from '@/api/accountsBrs';
import type { ExportCell } from '../exportReport';
import { Check, Loading, PeriodBar, ReportFrame } from '../reports/reportParts';
import { amt, num, td, tdate, th, today, useExcel, useKeyNav, usePeriod } from '../reports/reportUtils';
import { useVoucherOpen } from '../tds/useVoucherOpen';

/* Bank Reconciliation, as TallyPrime's BRS for a bank ledger as on the To date: the vouchers the bank
   hasn't cleared (a bank date from the statement, or typed in), the statement lines not in the books,
   and Balance as per Company Books → Balance as per Bank, checked against the statement's closing balance. */

const key = (...x: unknown[]) => ['acc-books', 'brs', ...x];
const label = 'mb-1 block text-xs font-semibold text-[var(--text-secondary)]';
const inr = (n: number) => `₹${amt(n) || '0.00'}`;
const BANK_KEY = 'grhamoy.brs.bank';

export function BrsPage() {
  const [p] = usePeriod();
  const qc = useQueryClient(); const toast = useToast(); const confirmDlg = useConfirm();
  const banksQ = useQuery({ queryKey: key('banks'), queryFn: getBrsBanks });
  const [bank, setBank] = useState<number | null>(() => { try { return Number(localStorage.getItem(BANK_KEY)) || null; } catch { return null; } });
  const banks = banksQ.data ?? [];
  const ledger = bank && banks.some((b) => b.id === bank) ? bank : banks[0]?.id ?? null;
  useEffect(() => { if (ledger) try { localStorage.setItem(BANK_KEY, String(ledger)); } catch { /* storage blocked */ } }, [ledger]);
  const [all, setAll] = useState(false);
  const q = useQuery({ queryKey: key('screen', ledger, p.from, p.to, all), queryFn: () => getBrs({ ledger: ledger!, from: p.from, to: p.to, all }), enabled: !!ledger });
  const upQ = useQuery({ queryKey: key('uploads', ledger), queryFn: () => getBrsUploads(ledger!), enabled: !!ledger });
  const [uploading, setUploading] = useState(false);
  const [matching, setMatching] = useState<BrsLine | null>(null);
  const v = useVoucherOpen();
  const excel = useExcel();
  const refresh = () => qc.invalidateQueries({ queryKey: key() });
  const err = (what: string) => (e: unknown) => toast.push(apiError(e, what), 'error');
  const date = useMutation({ mutationFn: setBankDate, onSuccess: refresh, onError: err('Could not set the bank date') });
  const clear = useMutation({ mutationFn: (vid: number) => clearBankDate(ledger!, vid), onSuccess: refresh, onError: err('Could not clear the bank date') });
  const rematch = useMutation({ mutationFn: () => rematchBank(ledger!), onSuccess: (r) => { toast.push(r.matched ? `${r.matched} matched with the statement` : 'Nothing new to match', 'success'); refresh(); }, onError: err('Matching failed') });
  const delLine = useMutation({ mutationFn: deleteStatementLine, onSuccess: () => { toast.push('Statement line removed', 'success'); refresh(); }, onError: err('Could not remove the line') });
  const del = useMutation({ mutationFn: deleteStatement, onSuccess: (r) => { toast.push(`Statement removed${r.cleared ? ` — ${r.cleared} bank date${r.cleared === 1 ? '' : 's'} taken back` : ''}`, 'success'); refresh(); }, onError: err('Delete failed') });

  const d = q.data;
  const rows = useMemo(() => d?.rows ?? [], [d]);
  const vOpen = v.open;
  const acts = useMemo(() => rows.map((r) => vOpen(r.voucherId)), [rows, vOpen]);
  const { row } = useKeyNav(acts);
  const dc = (n: number) => <><td className={num}>{n > 0 ? amt(n) : ''}</td><td className={num}>{n < 0 ? amt(n) : ''}</td></>;

  return (
    <>
      <PeriodBar onExcel={d ? () => excel(`BRS_${d.ledger.name}`, 'Bank Reconciliation', ['Date', 'Particulars', 'Vch Type', 'Vch No.', 'Transaction Type', 'Instrument No.', 'Instrument Date', 'Bank Date', 'Debit', 'Credit'], [
        ...rows.map((r) => [tdate(r.date), r.particulars, r.type, r.voucherNo, r.mode ?? '', r.refNo ?? '', tdate(r.instrDate), tdate(r.bankDate), r.amount > 0 ? r.amount : null, r.amount < 0 ? -r.amount : null] as ExportCell[]),
        ...d.bankOnly.map((l) => [tdate(l.date), l.description, '', '', 'Bank statement', l.refNo ?? '', '', tdate(l.date), l.amount > 0 ? l.amount : null, l.amount < 0 ? -l.amount : null] as ExportCell[]),
        ['', 'Balance as per Company Books', '', '', '', '', '', '', d.books > 0 ? d.books : null, d.books < 0 ? -d.books : null],
        ['', 'Amounts not reflected in Bank', '', '', '', '', '', '', d.notInBank.dr, d.notInBank.cr],
        ['', 'Amounts not reflected in Company Books', '', '', '', '', '', '', d.notInBooks.dr, d.notInBooks.cr],
        ['', 'Balance as per Bank', '', '', '', '', '', '', d.bank > 0 ? d.bank : null, d.bank < 0 ? -d.bank : null],
      ]) : undefined}>
        <FilterField label="Bank">
          <Select value={ledger ?? ''} onChange={(e) => setBank(Number(e.target.value))}>
            {banks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        </FilterField>
        <Check label="Show reconciled vouchers too" checked={all} onChange={setAll} />
      </PeriodBar>

      {!banksQ.isLoading && !banks.length ? <p className="py-8 text-center text-sm text-[var(--text-muted)]">No bank account — create a ledger under Bank Accounts first.</p> : (
        <>
          {/* Statements uploaded for this bank, upload, match again. */}
          <div className="mb-2 rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-2.5 text-xs print:hidden">
            <div className="flex flex-wrap items-center gap-2">
              <b>Statements:</b>
              {(upQ.data ?? []).slice(0, 6).map((u) => (
                <span key={u.id} className="inline-flex items-center gap-1 rounded-full border border-[var(--border-color)] px-2 py-0.5" title={`${u.fileName} · ${u.rows} new lines, ${u.duplicates} already there · ${u.uploadedBy ?? ''}`}>
                  {u.from ? `${tdate(u.from)} – ${tdate(u.to)}` : u.fileName} · {u.rows}
                  <button type="button" className="text-danger" title="Remove" onClick={async () => { if (await confirmDlg(`Remove the statement ${u.fileName}? Bank dates it gave are taken back.`)) del.mutate(u.id); }}><Trash2 size={12} /></button>
                </span>
              ))}
              {!upQ.data?.length && <span className="text-[var(--text-muted)]">none yet</span>}
              <span className="grow" />
              <Button variant="glass" size="sm" disabled={!ledger || rematch.isPending || !upQ.data?.length} onClick={() => rematch.mutate()}>{rematch.isPending ? 'Matching…' : 'Match again'}</Button>
              <Button variant="primary" size="sm" disabled={!ledger} onClick={() => setUploading(true)}><Upload size={14} /> Upload statement</Button>
            </div>
            <p className="mt-1 text-[11px] text-[var(--text-muted)]">Net banking → account statement → download as Excel (.xlsx) or CSV. Lines already uploaded are skipped; each line is matched with a voucher of the same amount (cheque / UTR no. first, else the nearest date).</p>
          </div>

          <Loading q={q}>
            {d && (
              <>
                <div className="mb-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {([['Balance as per books', d.books, 'text-accent'], ['Deposits not credited', d.notInBank.dr, 'text-warning'], ['Cheques not presented', d.notInBank.cr, 'text-accent'], ['Balance as per bank', d.bank, 'text-success']] as const).map(([l, x, c]) => (
                    <div key={l} className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-2.5">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">{l}</div>
                      <div className={clsx('mt-0.5 font-mono text-sm font-extrabold tabular-nums', c)}>{inr(Math.abs(x))}{x < 0 ? ' Cr' : ''}</div>
                    </div>
                  ))}
                </div>
                <ReportFrame title={`Bank Reconciliation · ${d.ledger.name}`} right={`as on ${tdate(d.to)}`}
                  note={all ? 'Uncleared vouchers up to the To date, and those cleared in the period. Type a bank date to clear one by hand; click a line to open its voucher.' : 'Vouchers the bank hasn’t cleared up to the To date. Type a bank date to clear one by hand; click a line to open its voucher.'}>
                  <table className="w-full min-w-[900px] text-xs">
                    <thead>
                      <tr className="border-b border-[var(--border-color)] text-left">
                        {['Date', 'Particulars', 'Vch Type', 'Transaction Type', 'Instrument No.', 'Instrument Date', 'Bank Date'].map((h) => <th key={h} className={th}>{h}</th>)}
                        <th className={`${th} text-right`}>Debit</th><th className={`${th} text-right`}>Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={r.voucherId} {...row(i)}>
                          <td className={`${td} whitespace-nowrap`}>{tdate(r.date)}</td>
                          <td className={td}><span className="font-semibold">{r.particulars}</span><div className="font-mono text-[10.5px] text-[var(--text-muted)]">{r.voucherNo}</div></td>
                          <td className={td}>{r.type}</td>
                          <td className={td}>{r.mode ?? ''}</td>
                          <td className={`${td} font-mono`}>{r.refNo ?? ''}</td>
                          <td className={`${td} whitespace-nowrap`}>{tdate(r.instrDate)}</td>
                          <td className={td} onClick={(e) => e.stopPropagation()}><BankDateCell r={r} max={today()} onSet={(bd) => date.mutate({ ledgerId: ledger!, voucherId: r.voucherId, bankDate: bd })} onClear={() => clear.mutate(r.voucherId)} /></td>
                          {dc(r.amount)}
                        </tr>
                      ))}
                      {!rows.length && <tr><td colSpan={9} className={clsx(td, 'py-6 text-center text-[var(--text-muted)]')}>Every voucher up to this date is reconciled.</td></tr>}
                      {d.bankOnly.length > 0 && <tr className="border-b border-[var(--border-color)] bg-accent/5"><td colSpan={9} className={`${td} font-bold`}>Amounts not reflected in Company Books (from the bank statement)</td></tr>}
                      {d.bankOnly.map((l) => (
                        <tr key={`l-${l.id}`} className="border-b border-[var(--border-color)]">
                          <td className={`${td} whitespace-nowrap`}>{tdate(l.date)}</td>
                          <td className={td}><span className="font-semibold">{l.description}</span></td>
                          <td className={`${td} text-[var(--text-muted)]`}>—</td>
                          <td className={td}>Bank statement</td>
                          <td className={`${td} font-mono`}>{l.refNo ?? ''}</td>
                          <td className={td} />
                          <td className={td}>
                            <span className="inline-flex items-center gap-1">
                              <Button variant="glass" size="sm" onClick={() => setMatching(l)}>Match…</Button>
                              <button type="button" className="rounded p-1 text-[var(--text-muted)] hover:text-danger" title="Remove this line (e.g. before the books started — already in the opening balance)"
                                onClick={async () => { if (await confirmDlg(`Remove the statement line of ${tdate(l.date)} (${l.description})?`)) delLine.mutate(l.id); }}><Trash2 size={12} /></button>
                            </span>
                          </td>
                          {dc(l.amount)}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-[var(--border-color)] font-semibold"><td className={td} colSpan={7}>Balance as per Company Books</td>{dc(d.books)}</tr>
                      <tr className="font-semibold"><td className={td} colSpan={7}>Amounts not reflected in Bank</td><td className={num}>{amt(d.notInBank.dr)}</td><td className={num}>{amt(d.notInBank.cr)}</td></tr>
                      <tr className="font-semibold"><td className={td} colSpan={7}>Amounts not reflected in Company Books</td><td className={num}>{amt(d.notInBooks.dr)}</td><td className={num}>{amt(d.notInBooks.cr)}</td></tr>
                      <tr className="border-t-2 border-[var(--text-primary)] font-extrabold"><td className={td} colSpan={7}>Balance as per Bank</td>{dc(d.bank)}</tr>
                    </tfoot>
                  </table>
                </ReportFrame>
                {d.statement && (
                  <p className={clsx('mt-2 rounded-card border p-2 text-xs', Math.abs(d.statement.difference) < 0.005 ? 'border-success/40 bg-success/10 text-success' : 'border-danger/40 bg-danger/10 text-danger')}>
                    Closing balance as per statement on {tdate(d.statement.date)}: <b>{inr(Math.abs(d.statement.balance))}{d.statement.balance < 0 ? ' (overdrawn)' : ''}</b>
                    {Math.abs(d.statement.difference) < 0.005 ? ' — agrees with Balance as per Bank.' : <> — differs by <b>{inr(Math.abs(d.statement.difference))}</b>. Check the bank dates typed by hand, statements not uploaded, or the opening balance.</>}
                  </p>
                )}
                {!d.uploads && <p className="mt-2 text-[11px] text-[var(--text-muted)]">No statement uploaded for this bank yet — upload one to match, or type the bank dates by hand.</p>}
              </>
            )}
          </Loading>
        </>
      )}
      {uploading && ledger && <UploadModal ledgerId={ledger} bankName={banks.find((b) => b.id === ledger)?.name ?? ''} onClose={() => setUploading(false)} onDone={refresh} />}
      {matching && d && <MatchModal line={matching} rows={rows.filter((r) => !r.lineId && Math.abs(r.amount - matching.amount) < 0.005)} onClose={() => setMatching(null)}
        onPick={(r) => date.mutate({ ledgerId: ledger!, voucherId: r.voucherId, lineId: matching.id }, { onSuccess: () => setMatching(null) })} />}
      {v.el}
    </>
  );
}

/** Bank date: from the statement (with a clear), or typed in. */
function BankDateCell({ r, max, onSet, onClear }: { r: BrsRow; max: string; onSet: (d: string) => void; onClear: () => void }) {
  if (r.lineId) {
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap" title="Matched with the bank statement">
        <span className="font-semibold text-success">{tdate(r.bankDate)}</span>
        <button type="button" className="rounded p-0.5 text-[var(--text-muted)] hover:text-danger" title="Clear the match" onClick={onClear}><X size={12} /></button>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <Input type="date" className="!h-7 !w-[132px] !px-1.5 !text-[11px]" max={max} value={r.bankDate ?? ''} onChange={(e) => e.target.value && e.target.value !== r.bankDate && onSet(e.target.value)} />
      {r.bankDate && <button type="button" className="rounded p-0.5 text-[var(--text-muted)] hover:text-danger" title="Clear the bank date" onClick={onClear}><X size={12} /></button>}
    </span>
  );
}

/** A statement line not in the books, matched with a voucher of the same amount. */
function MatchModal({ line, rows, onPick, onClose }: { line: BrsLine; rows: BrsRow[]; onPick: (r: BrsRow) => void; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title="Match the statement line" size="md" footer={<Button variant="glass" onClick={onClose}>Close</Button>}>
      <p className="mb-2 text-xs"><b>{tdate(line.date)}</b> · {line.description}{line.refNo ? ` · ${line.refNo}` : ''} · <b>{inr(Math.abs(line.amount))} {line.amount > 0 ? 'in' : 'out'}</b></p>
      {rows.length ? (
        <table className="w-full text-xs">
          <thead><tr className="border-b border-[var(--border-color)] text-left"><th className={th}>Date</th><th className={th}>Voucher</th><th className={th}>Particulars</th><th className={th}>Instrument</th><th className={th} /></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.voucherId} className="border-b border-[var(--border-color)]">
                <td className={`${td} whitespace-nowrap`}>{tdate(r.date)}</td><td className={`${td} font-mono`}>{r.voucherNo}</td><td className={td}>{r.particulars}</td>
                <td className={td}>{[r.mode, r.refNo].filter(Boolean).join(' ')}</td>
                <td className={`${td} text-right`}><Button variant="primary" size="sm" onClick={() => onPick(r)}>Match</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <p className="text-xs text-[var(--text-muted)]">No uncleared voucher of {inr(Math.abs(line.amount))} {line.amount > 0 ? 'received' : 'paid'} up to this date. Book it (e.g. bank charges as a Payment, interest as a Receipt), then press Match again.</p>}
    </Modal>
  );
}

/** Upload: the file → its columns mapped (this bank's saved mapping, else a guess) → import. */
function UploadModal({ ledgerId, bankName, onClose, onDone }: { ledgerId: number; bankName: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [m, setM] = useState<Partial<BankMapping> | null>(null);
  const prev = useMutation({ mutationFn: (f: File) => previewStatement(f, ledgerId), onSuccess: (r) => setM({ dateOrder: 'DMY', ...r.mapping }), onError: (e) => toast.push(apiError(e, 'Could not read the file'), 'error') });
  const imp = useMutation({
    mutationFn: () => importStatement(file!, ledgerId, m as BankMapping),
    onSuccess: (r) => { toast.push(r.added ? `${r.added} line${r.added === 1 ? '' : 's'} added${r.duplicates ? ` (${r.duplicates} already there)` : ''} · ${r.matched} matched` : `Nothing new — all ${r.duplicates} lines were uploaded before`, 'success'); onDone(); onClose(); },
    onError: (e) => toast.push(apiError(e, 'Import failed'), 'error'),
  });
  const p = prev.data;
  const pick = (k: keyof BankMapping, l: string, need = false) => (
    <div>
      <label className={label}>{l}{need && ' *'}</label>
      <Select value={(m?.[k] as string) ?? ''} onChange={(e) => setM({ ...m, [k]: e.target.value || null })}>
        <option value="">{need ? '— choose —' : '— none —'}</option>
        {p?.headers.map((h) => <option key={h} value={h}>{h}</option>)}
      </Select>
    </div>
  );
  const ready = !!m?.date && !!m.description && (m.mode === 'split' ? !!m.debit && !!m.credit : !!m?.amount);
  return (
    <Modal open onClose={onClose} title={`Upload statement — ${bankName}`} size="lg"
      footer={<><Button variant="glass" onClick={onClose}>Cancel</Button><Button variant="primary" disabled={!p || !ready || imp.isPending} onClick={() => imp.mutate()}>{imp.isPending ? 'Importing…' : 'Import'}</Button></>}>
      <input ref={fileRef} type="file" accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) { setFile(f); setM(null); prev.mutate(f); } }} />
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Button variant="glass" size="sm" onClick={() => fileRef.current?.click()}><Upload size={14} /> {file ? 'Choose another file' : 'Choose file (.xlsx or .csv)'}</Button>
        {file && <span className="font-mono">{file.name}</span>}
        {prev.isPending && <span className="text-[var(--text-muted)]">Reading…</span>}
      </div>
      {p && m && (
        <div className="mt-3 space-y-3">
          <p className="text-[11px] text-[var(--text-muted)]">Headings found on row {p.headerRow}. {p.fromSaved ? 'This bank’s saved mapping is filled in.' : 'The columns below are a guess — check them; they are saved for this bank.'}</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {pick('date', 'Date', true)}
            {pick('description', 'Description / Narration', true)}
            {pick('ref', 'Reference / Cheque no.')}
            {pick('valueDate', 'Value date')}
            {pick('balance', 'Balance')}
            <div>
              <label className={label}>Dates written as</label>
              <Select value={m.dateOrder ?? 'DMY'} onChange={(e) => setM({ ...m, dateOrder: e.target.value as BankMapping['dateOrder'] })}>
                <option value="DMY">Day / Month / Year</option><option value="MDY">Month / Day / Year</option><option value="YMD">Year / Month / Day</option>
              </Select>
            </div>
          </div>
          <div className="flex flex-wrap gap-1 text-xs">
            {([['split', 'Withdrawal and Deposit columns'], ['single', 'One Amount column']] as const).map(([k, l]) => (
              <button key={k} type="button" onClick={() => setM({ ...m, mode: k })}
                className={clsx('h-7 rounded-md border px-2 text-[11px] font-semibold', m.mode === k ? 'border-accent bg-accent text-white' : 'border-[var(--border-color)] text-[var(--text-secondary)]')}>{l}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {m.mode === 'split' ? <>{pick('debit', 'Withdrawal (Debit)', true)}{pick('credit', 'Deposit (Credit)', true)}</> : <>{pick('amount', 'Amount', true)}{pick('drcr', 'Dr / Cr (if a column)')}</>}
          </div>
          <div className="overflow-x-auto rounded-card border border-[var(--border-color)]">
            <table className="w-full text-[11px]">
              <thead><tr className="border-b border-[var(--border-color)]">{p.headers.map((h) => <th key={h} className={clsx(th, 'whitespace-nowrap text-left', Object.values(m).includes(h) && 'text-accent')}>{h}</th>)}</tr></thead>
              <tbody>{p.sample.map((r, i) => <tr key={i} className="border-b border-[var(--border-color)]">{r.map((c, j) => <td key={j} className="whitespace-nowrap px-2 py-1">{c}</td>)}</tr>)}</tbody>
            </table>
          </div>
          <p className="text-[11px] text-[var(--text-muted)]">Lines without a date (opening balance, totals, footers) are skipped. A single Amount column: a minus sign or Dr means money out.</p>
        </div>
      )}
    </Modal>
  );
}
